import { arrayMove } from '@dnd-kit/sortable';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';

import {
  isTabAllowed,
  selectImpersonatedUserId,
  useGetLoggedInUser,
  useImpersonationSelector,
} from '@revelio/auth';

import { allPreferencesQuery } from '../../../shared/user-preferences/api/all-preferences-query';
import { useCanSavePreferences } from '../../../shared/user-preferences/hooks/useCanSavePreferences';
import {
  PreferenceScope,
  UserPreferenceKey,
  type UserPreferences,
} from '../../../shared/user-preferences/types';
import {
  enqueuePageOrderSave,
  writePageOrderToCache,
} from '../../../shared/user-preferences/utils/page-order-queue';
import {
  type Page,
  PageType,
  type RevelioPageId,
} from '../components/PageConfigProvider/types';
import { usePageManager } from '../components/PageManagerProvider/PageManagerProvider';
import { PAGES_MENU_TRIGGER_ID } from '../components/tab-drag-collision';
import { getLocalTemplate } from '../utils/page-config-lookup';
import {
  type AnalyticsEntity,
  getAnalyticsViewsForEntity,
  TAB_STRIP_VIEWS,
} from '../utils/revelio-primary-filter-entity-switch';
import {
  buildPathFromPage,
  entityTypeToFilterId,
} from '../utils/routing-helpers';

const MAX_BAR_TABS = 4;

type TabKind = 'analytics' | 'custom';

export type TabItem = {
  id: string;
  kind: TabKind;
  label: string;
  path: string;
  revelioPageId?: RevelioPageId;
  /** Original payload for callers that need it (e.g. menu rename/delete). */
  page?: Page;
};

const entityToScope = (entity: AnalyticsEntity): PreferenceScope => {
  const map: Record<AnalyticsEntity, PreferenceScope> = {
    company: PreferenceScope.COMPANY,
    industry: PreferenceScope.INDUSTRY,
    geography: PreferenceScope.GEOGRAPHY,
    role: PreferenceScope.ROLE,
    skill: PreferenceScope.SKILL,
    activity: PreferenceScope.ACTIVITY,
  };
  return map[entity];
};

const analyticsTabId = (viewName: string) => `analytics:${viewName}`;

const analyticsTabLabel = (revelioPageId: RevelioPageId): string | undefined =>
  getLocalTemplate(revelioPageId)?.name;
const customTabId = (pageId: string) => `custom:${pageId}`;

// Stable reference so a render where allPrefs lacks data doesn't churn the
// downstream useMemo deps with a fresh empty array each pass.
const EMPTY_PAGE_ORDER: string[] = [];

const mergeWithStoredOrder = (
  available: TabItem[],
  storedIds: string[]
): TabItem[] => {
  const byId = new Map(available.map((t) => [t.id, t]));
  const seen = new Set<string>();
  const ordered: TabItem[] = [];
  for (const id of storedIds) {
    const tab = byId.get(id);
    if (tab && !seen.has(id)) {
      ordered.push(tab);
      seen.add(id);
    }
  }
  for (const tab of available) {
    if (!seen.has(tab.id)) {
      ordered.push(tab);
    }
  }
  return ordered;
};

export const useTabOrder = (entity: AnalyticsEntity) => {
  const { loggedInUser } = useGetLoggedInUser();
  const { pages } = usePageManager();
  const queryClient = useQueryClient();

  const scope = entityToScope(entity);
  const impersonatedUserId = useImpersonationSelector(selectImpersonatedUserId);
  const canReorder = useCanSavePreferences();

  const { data: allPrefs } = useQuery(allPreferencesQuery(impersonatedUserId));

  const availableTabs = useMemo<TabItem[]>(() => {
    const userTabs = loggedInUser?.tabs ?? [];
    const analytics: TabItem[] = getAnalyticsViewsForEntity(
      TAB_STRIP_VIEWS,
      entity
    )
      .filter((view) => isTabAllowed(view.serverTab, userTabs))
      .map((view) => ({
        id: analyticsTabId(view.name),
        kind: 'analytics',
        label: analyticsTabLabel(view.revelioPageId) ?? view.name,
        path: `/${view.path}`,
        revelioPageId: view.revelioPageId,
      }));

    const entityFilterId = entityTypeToFilterId(entity);
    const custom: TabItem[] = pages
      .filter(
        (p) =>
          p.type !== PageType.REPORT &&
          p.tabs?.[0]?.tabContent?.primaryFilter?.id === entityFilterId
      )
      .map((page) => ({
        id: customTabId(page.id),
        kind: 'custom',
        label: page.name,
        path: buildPathFromPage(page),
        page,
      }));

    return [...analytics, ...custom];
  }, [loggedInUser?.tabs, pages, entity]);

  // Holds the in-flight order while a drag is active.
  const [draftIds, setDraftIds] = useState<string[] | null>(null);

  const storedIds =
    allPrefs?.[scope]?.[UserPreferenceKey.PageOrder] ?? EMPTY_PAGE_ORDER;

  type AllPrefs = Record<PreferenceScope, Partial<UserPreferences>>;
  type PersistVars = {
    ids: string[];
    targetScope: PreferenceScope;
    previous: AllPrefs | undefined;
    sequence: number;
  };

  // Bumps on every persist so onError/onSettled effects from stale (older)
  // mutations can no-op — prevents an older save's rollback or refetch from
  // clobbering a newer optimistic write.
  const sequenceRef = useRef(0);

  const { mutate: persistOrder } = useMutation<unknown, Error, PersistVars>({
    mutationFn: ({ ids, targetScope }) =>
      enqueuePageOrderSave(targetScope, ids),
    onError: (_err, { previous, sequence }) => {
      if (sequence !== sequenceRef.current) return;
      if (previous) queryClient.setQueryData(['user-preferences'], previous);
    },
    onSettled: (_data, _err, { sequence }) => {
      if (sequence !== sequenceRef.current) return;
      queryClient.invalidateQueries({ queryKey: ['user-preferences'] });
    },
  });

  const dispatchPersist = useCallback(
    (ids: string[]) => {
      if (!canReorder) return;
      const previous = writePageOrderToCache(queryClient, scope, ids);
      sequenceRef.current += 1;
      persistOrder({
        ids,
        targetScope: scope,
        previous,
        sequence: sequenceRef.current,
      });
    },
    [queryClient, persistOrder, scope, canReorder]
  );

  const activeIds = draftIds ?? storedIds;

  const orderedTabs = useMemo(
    () => mergeWithStoredOrder(availableTabs, activeIds),
    [availableTabs, activeIds]
  );

  // Reset is a no-op when the list already matches the natural order (analytics
  // views first, then custom pages). Compare against that order rather than
  // "is anything stored", so dragging tabs back into place also clears the flag.
  const isCustomOrder = orderedTabs.some(
    (tab, index) => tab.id !== availableTabs[index]?.id
  );

  // Bar holds only the official Revelio template pages (custom pages overflow); revisit once new pages replace the removed overview pages.
  const barLimit = Math.min(
    MAX_BAR_TABS,
    orderedTabs.filter((t) => t.kind === 'analytics').length
  );
  const barTabs = orderedTabs.slice(0, barLimit);
  const menuTabs = orderedTabs.slice(barLimit);

  const previewMove = useCallback(
    (activeId: string, overId: string) => {
      // The menu trigger droppable only auto-opens the popover. Skipping the
      // reorder here keeps the bar stable at the bar/trigger boundary where
      // collision detection would otherwise oscillate the tab in and out.
      if (overId === PAGES_MENU_TRIGGER_ID) return;
      const fromIdx = orderedTabs.findIndex((t) => t.id === activeId);
      if (fromIdx < 0) return;
      const targetIdx = orderedTabs.findIndex((t) => t.id === overId);
      if (targetIdx < 0 || targetIdx === fromIdx) return;
      const next = arrayMove(orderedTabs, fromIdx, targetIdx).map((t) => t.id);
      setDraftIds(next);
    },
    [orderedTabs]
  );

  /** Persist the current drag order to the server (optimistic — does not wait). */
  const saveOrder = useCallback(() => {
    const finalIds = draftIds ?? storedIds;
    setDraftIds(null);
    dispatchPersist(finalIds);
  }, [draftIds, storedIds, dispatchPersist]);

  /**
   * Drop a tab on the menu trigger without hovering a specific menu slot:
   * move it to the first menu position so the action is meaningful.
   */
  const commitDropOnMenu = useCallback(
    (tabId: string) => {
      const fromIdx = orderedTabs.findIndex((t) => t.id === tabId);
      if (fromIdx < 0 || fromIdx >= barLimit) {
        saveOrder();
        return;
      }
      const finalIds = arrayMove(orderedTabs, fromIdx, barLimit).map(
        (t) => t.id
      );
      setDraftIds(null);
      dispatchPersist(finalIds);
    },
    [orderedTabs, barLimit, saveOrder, dispatchPersist]
  );

  /** Resets tab order to the default by clearing the stored order; tabs then revert to their natural order (analytics views first, then custom pages). */
  const resetOrder = useCallback(() => {
    setDraftIds(null);
    dispatchPersist([]);
  }, [dispatchPersist]);

  /** Discard the drag preview without persisting. */
  const cancelPreview = useCallback(() => setDraftIds(null), []);

  return {
    orderedTabs,
    barTabs,
    menuTabs,
    previewMove,
    saveOrder,
    commitDropOnMenu,
    cancelPreview,
    resetOrder,
    /** True when the current order differs from the natural default order. */
    isCustomOrder,
    /** False while impersonating: the order shown is another user's. */
    canReorder,
  };
};
